import { defineStore } from 'pinia';
import { ref, computed, reactive, watch } from 'vue';
import api from "src/services/api";
import merge from "deepmerge";
import extend from "just-extend"
import { notify } from '../utils/utils'
import {Mutex} from 'async-mutex';
const initWorkspacesApiMutex = new Mutex();
import {useRoute} from "vue-router";
import { LocalStorage } from 'quasar'

export const useWorkspacesStore = defineStore('workspaces', () => {
  // const teamMemberWorkspaces = reactive([])
  /**
   * @type {Ref<UnwrapRef<[] | Array<Workspace>>>}
   */
  const workspaces = ref([])
  /**
   * @type {Ref<UnwrapRef<{} | Workspace>>}
   */
  const active = ref({
    id: null,
    createTime: null,
    updateTime: null,
    name: null,
    color: null,
    retentionPolicy: {
      filesDownloadLinkTTL: null,
      filesTTL: null,
      submissionsTTL: null
    },
    accountId: null,
    customDomain: {
      domain: null,
      connected: false,
      postmarkId: null,
      DKIMVerified: null,
      DKIMHost: null,
      DKIMTextValue: null,
      ReturnPathDomainVerified: false,
      ReturnPathDomain: null,
      ReturnPathDomainCNAMEValue: null
    },
    addons: {
      powerUp: false,
      noBounceEmail: false,
      integrations: false,
      team: false,
      customDomain: false,
      extendedDataRetention: false,
      filesUpload: false
    }
  })
  /**
   * @type {Ref<UnwrapRef<{} | Workspace>>}
   */
  const edited = ref({})
  const workspacesRoles = ref({})
  const loadingWorkspaces = ref(false)
  const loadingActive = ref(false)
  const saved = ref(true)
  const saving = ref(false)
  const saveWaitTimeout = ref(1)

  function $reset() {
    workspaces.value = []
    edited.value = {}
    workspacesRoles.value = {}
    loadingWorkspaces.value = false
    loadingActive.value =  false
    saved.value =  true
    saving.value = false
    saveWaitTimeout.value = 1
    active.value = {
      id: null,
      createTime: null,
      updateTime: null,
      name: null,
      color: null,
      retentionPolicy: {
        filesDownloadLinkTTL: null,
        filesTTL: null,
        submissionsTTL: null
      },
      accountId: null,
      customDomain: {
        domain: null,
        connected: false,
        postmarkId: null,
        DKIMVerified: null,
        DKIMHost: null,
        DKIMTextValue: null,
        ReturnPathDomainVerified: false,
        ReturnPathDomain: null,
        ReturnPathDomainCNAMEValue: null
      },
      addons: {
        powerUp: false,
        noBounceEmail: false,
        integrations: false,
        team: false,
        customDomain: false,
        extendedDataRetention: false,
        filesUpload: false,
        formBranding: false,
      }
    }
  }

  const effectiveAddons = computed(() => ({
    noBounceEmail: !!active.value?.addons?.noBounceEmail || !!active.value?.addons?.powerUp,
    integrations: !!active.value?.addons?.integrations || !!active.value?.addons?.powerUp,
    team: !!active.value?.addons?.team || !!active.value?.addons?.powerUp,
    customDomain: !!active.value?.addons?.customDomain || !!active.value?.addons?.powerUp,
    extendedDataRetention: !!active.value?.addons?.extendedDataRetention || !!active.value?.addons?.powerUp,
    filesUpload: !!active.value?.addons?.filesUpload || !!active.value?.addons?.powerUp,
    formBranding: !!active.value?.addons?.formBranding || !!active.value?.addons?.powerUp
  }))

  function endLoading() {
    setTimeout(() => {
      //doing this in timeout to make sure that the watchers of previous lines finished running
      //otherwise auto-save will be triggered immediately after loading a form
      loadingActive.value = false
    }, 10)
  }

  function endLoadingWorkspaces() {
    setTimeout(() => {
      //doing this in timeout to make sure that the watchers of previous lines finished running
      //otherwise auto-save will be triggered immediately after loading a form
      loadingWorkspaces.value = false
    }, 10)
  }

  async function update() {
    loadingActive.value = true // guard against triggering autosave due to response from save
    const updated = await api.updateWorkspace(edited.value)
    if (updated) {
      workspaces.value[workspaces.value.findIndex(w => w.id === edited.value.id)] = updated
      if (updated.id !== active.value.id) {
        endLoading.call(this)
        //the user switched ws before the save was completed
        return
      }
      active.value = merge(active.value, updated)
      edited.value = merge({}, updated)
    } else {
      //reset changes
      notify('Saving has failed. Please try again or contact support.', {type: 'negative'})
      edited.value = merge({}, active.value)
    }

    endLoading.call(this)

    // const res = await api.updateWorkspace(edited.id, edited)
    // active.value = res
    // workspaces[workspaces.findIndex(w => w.id === edited.id)] = res
    // dirty.value = false
  }

  async function loadWorkspaces(force, routeWorkspaceId) {
    const release = await initWorkspacesApiMutex.acquire();
    try {
      if (active.value?.id && !force) {
        return
      }
      // todo do not enter here if we're in the middle of loading. should not return immediately though - need to wait until loading has finished, and then return
      loadingWorkspaces.value = true
      const teamMemberWorkspaces = await api.listWorkspaces()
      workspaces.value.splice(0, workspaces.value.length, ...teamMemberWorkspaces.map(ws => ws.workspace));
      Object.keys(workspacesRoles.value).forEach(key => delete workspacesRoles.value[key]);
      teamMemberWorkspaces.forEach(tmws => {
        workspacesRoles.value[tmws.workspace.id] = tmws.member
      })
      loadingActive.value = true
      let lastActiveWorkspace
      try {
        lastActiveWorkspace = LocalStorage.getItem('lastActiveWorkspace')
        if (lastActiveWorkspace === 'null' || lastActiveWorkspace === 'undefined' || lastActiveWorkspace === '') {
          lastActiveWorkspace = undefined
        }
      } catch (e) {
        // data wasn't successfully saved due to
        // a Web Storage API error
      }
      if (workspaces.value.length) {
        // const route = useRoute()
        // use wsid from the router, if non exist, use the first in the list
        if (routeWorkspaceId) {
          const ws = workspaces.value.find(ws => ws.id === routeWorkspaceId)
          if (ws) {
            active.value = ws
          }
        }
        if (!active.value?.id && lastActiveWorkspace) {
          const ws = workspaces.value.find(ws => ws.id === lastActiveWorkspace)
          if (ws) {
            active.value = ws
          }
        }
        if (!active.value?.id) {
          active.value = workspaces.value[0]
        }
        edited.value = merge({}, active.value)
        try {
          LocalStorage.set('lastActiveWorkspace', active.value.id)
        } catch (e) {
          // do nothing.
        }
      }
      endLoading.call(this)
      endLoadingWorkspaces.call(this)
    } finally {
      release()
    }
  }

  async function setActive (workspaceId) {
    loadingActive.value = true
    // const workspace = await api.getWorkspace(workspaceId)
    if (!workspaces.value.length) {
      await loadWorkspaces()
    }
    if (active.value?.id !== workspaceId) {
      const workspace = workspaces.value.find(ws => ws.id === workspaceId)
      active.value = workspace
      edited.value = merge({}, active.value)
      try {
        LocalStorage.set('lastActiveWorkspace', active.value.id)
      } catch (e) {
        // do nothing.
      }
    }
    endLoading.call(this)
  }

  /**
   * @param {Workspace} workspace
   */
  async function createWorkspace (workspace) {
    // todo: save workspace to db, and update state after success
    // todo: load/create team
    workspaces.value.push(workspace)
    active.value = workspace
    edited.value = merge({}, active.value)
  }

  async function connectCustomDomain(domain) {
    const wsId = active.value.id
    const dnsRequirements = await api.addCustomDomain(wsId, domain)
    /**
     * {
     *   domain: string
     *   DKIMPendingHost: string
     *   DKIMPendingTextValue: string
     *   ReturnPathDomain: string
     *   ReturnPathDomainCNAMEValue: string
     *   id: number
     * }
     */
    if (dnsRequirements) {
      const idx = workspaces.value.findIndex(w => w.id === wsId)
      workspaces.value[idx].customDomain = dnsRequirements
      if (wsId !== active.value.id) {
        //the user switched ws before the save was completed
        return
      }
      loadingActive.value = true // guard against triggering autosave
      active.value.customDomain = dnsRequirements
      edited.value.customDomain = dnsRequirements
      endLoading.call(this)
    }
  }

  async function disconnectCustomDomain() {
    const wsId = active.value.id
    const ok = await api.removeCustomDomain(wsId)
    if (ok) {
      const idx = workspaces.value.findIndex(w => w.id === wsId)
      workspaces.value[idx].customDomain = null
      if (wsId !== active.value.id) {
        //the user switched ws before the save was completed
        return
      }
      loadingActive.value = true // guard against triggering autosave
      active.value.customDomain = null
      edited.value.customDomain = null
      endLoading.call(this)
    }
  }

  /**
   *
   * @param {'DKIM'|'ReturnPath'} record
   * @returns {Promise<void>}
   */
  async function verifyCustomDomainDNS(record) {
    const wsId = active.value.id
    const domainVerification = await api.verifyCustomDomain(wsId, record)
    if (domainVerification) {
      const idx = workspaces.value.findIndex(w => w.id === wsId)
      console.log('ws custom domain', workspaces.value[idx].customDomain)
      const customDomain = extend({}, workspaces.value[idx].customDomain, domainVerification.customDomain)
      workspaces.value[idx].customDomain = null
      if (wsId !== active.value.id) {
        //the user switched ws before the save was completed
        return
      }
      loadingActive.value = true // guard against triggering autosave
      active.value.customDomain = customDomain
      edited.value.customDomain = customDomain
      endLoading.call(this)
    }
  }

  return {
    $reset,
    workspacesRoles,
    workspaces,
    edited,
    active,
    effectiveAddons,

    loadingActive,
    saved,
    saving,
    saveWaitTimeout,
    // Actions:
    loadWorkspaces,
    update,
    setActive,
    createWorkspace,
    connectCustomDomain,
    disconnectCustomDomain,
    verifyCustomDomainDNS
  }
})


/**
 * @typedef Workspace
 * @property {string} id
 * @property {string} createTime
 * @property {string} updateTime
 * @property {string} name
 * @property {string} color
 * @property {Object} retentionPolicy
 * @property {number} retentionPolicy.filesDownloadLinkTTL
 * @property {number} retentionPolicy.filesTTL
 * @property {number} retentionPolicy.submissionsTTL
 * @property {string} accountId
 * @property {Object?} customDomain
 * @property {string} customDomain.domain
 * @property {boolean} customDomain.connected
 * @property {string} customDomain.postmarkId
 * @property {boolean} customDomain.DKIMVerified
 * @property {string} customDomain.DKIMHost
 * @property {string} customDomain.DKIMTextValue
 * @property {boolean} customDomain.ReturnPathDomainVerified
 * @property {string} customDomain.ReturnPathDomain
 * @property {string} customDomain.ReturnPathDomainCNAMEValue
 * @property {Object?} addons
 * @property {boolean?} addons.powerUp
 * @property {boolean?} addons.noBounceEmail
 * @property {boolean?} addons.integrations
 * @property {boolean?} addons.team
 * @property {boolean?} addons.customDomain
 * @property {boolean?} addons.extendedDataRetention
 * @property {boolean?} addons.filesUpload
 * @property {boolean?} addons.formBranding
 */
